const React = require('react');
const T = require('prop-types');
const { default: styled, css } = require('styled-components');

const { default: Paper } = require('@mui/material/Paper');
const { default: MuiBadge } = require('@mui/material/Badge');
const { default: SearchIcon } = require('@mui/icons-material/Search');
const { default: TextField } = require('@mui/material/TextField');
const { default: FlatButton } = require('@mui/material/Button');
const SimpleDialog = require('components/SimpleDialog');

const LoadingScreen = require('components/LoadingScreen');

const { default: Classes } = require('./styles.scss');
const GroupHeader = require('../GroupHeader');
const SearchResultGrid = require('components/SearchResultGrid');
const useForm = require('hooks/useForm');
const { useRouteScrollRestoration } = require('hooks/useRouteScrollRestoration');
const { useClassDetailDialog } = require('./useClassDetailDialog');
const { useClassMemberLoading } = require('./useClassMemberLoading');
const { useClassMemberSearch } = require('./useClassMemberSearch');

const internals = {
    noop: () => {}
};

module.exports = ClassDetailPage;

const SearchWrapper = styled.div`
    padding: 14px 20px 14px;
    background: white;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    margin: 0 auto;
`;

const StyledSearchContainer = styled(Paper)`
    width: 100%;
    max-width: 560px;
    height: 52px;
    min-height: 52px;
    box-sizing: border-box;
    margin: auto;
    display: flex;
    align-items: center;
    background-color: ${({ theme }) => theme.palette.tertiary.main};
    color: ${({ theme }) => theme.palette.tertiary.contrastText};
    padding: 0 16px;
    cursor: text;
    border: none;
    border-radius: 8px;
    box-shadow: none !important;
    gap: 12px;

    *,
    input.MuiInputBase-input,
    input.MuiInputBase-input::placeholder {
        color: ${({ theme }) => theme.palette.tertiary.contrastText};
    }
`;

const StyledSearchIcon = styled(SearchIcon)`
    color: ${({ theme }) => theme.palette.tertiary.contrastText};
    flex: 0 0 auto;
`;

const StyledTextField = styled(TextField)`
    .MuiInputBase-root {
        padding: 0;
    }
    .MuiInputLabel-root {
        display: none;
    }
    .MuiInputBase-input {
        color: #ffffff;
        -webkit-text-fill-color: #ffffff;
        font-weight: 400;
        &::placeholder {
            color: #ffffff;
            -webkit-text-fill-color: #ffffff;
            opacity: 1;
            font-weight: 400;
        }
    }
    .MuiInput-underline:before,
    .MuiInput-underline:after {
        display: none;
    }
`;

function ClassDetailPage(props) {

    const {
        styles,
        GroupDetailsHeaderControlsForm,
        DetailsLoaderWrapper,
        DetailsLoadingPill,
        DetailsStateWrapper,
        DetailsStatePill
    } = internals;

    const {
        onClickLoadMore,
        setClassSearchText,
        showJoin,
        showLoadMore,
        roleGroupFilter,
        setRoleGroupFilter,
        activeRoleGroupFilter,
        availableRoleGroups,
        classSearchText,
        classDetails: {
            class: group,
            users: allClassmates
        },
        onSubmitSearch,
        resolveInitialClassmates,
        restoreScroll,
        rememberScroll
    } = props;

    const initialClassmatesLoading = useClassMemberLoading({
        classId: group.id,
        hasClassmates: !!allClassmates.length,
        loadClassmates: resolveInitialClassmates
    });
    const {
        classmates,
        groupSearchVal,
        hasActiveRoleGroupFilter,
        hasUsedSearch,
        onChangeRoleGroupFilter,
        onChangeSearch,
        submitting
    } = useClassMemberSearch({
        activeRoleGroupFilter,
        allClassmates,
        classId: group.id,
        initialSearchText: classSearchText,
        onSubmitSearch,
        roleGroupFilter,
        setClassSearchText,
        setRoleGroupFilter
    });
    const {
        cancelDialog,
        closeDialog,
        confirmDialog,
        dialog,
        openDialog
    } = useClassDetailDialog();

    useRouteScrollRestoration({
        rememberScroll,
        restoreScroll
    });

    const showEmptyState = !classmates.length;
    const showSearchLoader = submitting || (initialClassmatesLoading && showEmptyState);
    const shouldHideSearch = !hasUsedSearch
        && !groupSearchVal
        && showEmptyState
        && !hasActiveRoleGroupFilter
        && !showSearchLoader;
    const emptyStateMessage = groupSearchVal || hasActiveRoleGroupFilter
        ? 'No members match your search'
        : `There aren't any ${showJoin ? '' : ' other'} members of this group`;

    return <div className={Classes.wrapper}>
        <GroupHeader
            group={group}
            variant='details'
            openSimpleDialogWithProps={openDialog}
        />
        <GroupDetailsHeaderControlsForm
            availableRoleGroups={availableRoleGroups}
            roleGroupFilter={roleGroupFilter}
            setRoleGroupFilter={onChangeRoleGroupFilter}
        />
        {!shouldHideSearch && <SearchWrapper>
            <StyledSearchContainer>
                <StyledSearchIcon />
                <StyledTextField
                    id="class-member-search"
                    placeholder='Search names'
                    primary
                    fullWidth
                    inputProps={{
                        'aria-label': 'Search names'
                    }}
                    InputProps={{
                        disableUnderline: true,
                        style: { backgroundColor: 'transparent' }
                    }}
                    value={groupSearchVal}
                    onChange={onChangeSearch}
                />
            </StyledSearchContainer>
        </SearchWrapper>}
        {showSearchLoader && (
            <DetailsLoaderWrapper>
                <DetailsLoadingPill>Loading members...</DetailsLoadingPill>
                <LoadingScreen variant='content' />
            </DetailsLoaderWrapper>
        )}

        {!showSearchLoader && showEmptyState && (
            <DetailsStateWrapper>
                <DetailsStatePill>{emptyStateMessage}</DetailsStatePill>
            </DetailsStateWrapper>
        )}

        {!showEmptyState && (
            <SearchResultGrid
                results={classmates.filter((result) => !result.isMe)}
                isFromGroup
                groupId={group.id}
            />
        )}

        {!showEmptyState && !!showLoadMore && (
            <FlatButton
                // Yeah these are inline styles, we'll copy paste them for the upgrade branch
                style={{ ...styles.button, margin: '0 auto 32px' }}
                variant='contained'
                color='primary'
                onClick={() => onClickLoadMore(group.id)}
            >
                Load More Results
            </FlatButton>
        )}

        <SimpleDialog
            isOpen={dialog.isOpen}
            onRequestClose={closeDialog}
            title={dialog.title}
            content={dialog.content}
            buttons={<React.Fragment>
                <FlatButton
                    onClick={cancelDialog}
                    color='secondary'
                    variant='contained'
                    data-focus-outline='radius:40'
                    style={{ marginRight: '10px' }}
                >
                    {dialog.declineLabel || 'No'}
                </FlatButton>
                <FlatButton
                    onClick={confirmDialog}
                    variant='contained'
                    data-focus-outline='radius:40'
                >
                    {dialog.confirmLabel || 'Yes'}
                </FlatButton>
            </React.Fragment>}
        />
    </div>;
}

ClassDetailPage.propTypes = {
    history: T.object,
    onClickJoin: T.func,
    onClickLeave: T.func,
    onClickLoadMore: T.func,
    setClassSearchText: T.func,
    classSearchText: T.string,
    showJoin: T.bool,
    showLoadMore: T.bool,
    roleGroupFilter: T.string,
    setRoleGroupFilter: T.func,
    activeRoleGroupFilter: T.object,
    availableRoleGroups: T.arrayOf(T.object),
    classDetails: T.shape({
        class: T.object.isRequired,
        users: T.arrayOf(T.shape({
            id: T.any.isRequired,
            firstName: T.string.isRequired,
            lastName: T.string.isRequired,
            croppedPicture: T.string.isRequired
        })).isRequired
    }).isRequired,
    rolePermissions: T.shape({
        id: T.number,
        roleId: T.number,
        schoolId: T.number,
        homeText: T.string,
        canCreateAnnouncement: T.bool,
        canEditGroups: T.bool,
        canViewBadge: T.bool,
        canSeeChatMessagesNoJoin: T.bool
    }),
    role: T.shape({
        id: T.number,
        name: T.string,
        label: T.string
    }),
    classSearchCriteria: T.object,
    onSubmitSearch: T.func,
    resolveInitialClassmates: T.func,
    restoreScroll: T.func,
    rememberScroll: T.func
};

internals.asArray = (value) => Array.isArray(value) ? value : [];

internals.buildRoleOptions = (availableRoleGroups) => {

    const roleGroups = internals.asArray(availableRoleGroups);

    if (!roleGroups.length) {
        return [];
    }

    return [{
        value: 'all',
        label: 'All'
    }].concat(roleGroups.map((option) => ({
        value: option.name,
        label: option.label || option.name
    })));
};

internals.resolveRoleGroupFilterValue = ({ availableRoleGroups, roleGroupFilter }) => {

    const roleGroups = internals.asArray(availableRoleGroups);

    if (!roleGroups.length) {
        return '';
    }

    const matchingRoleGroup = roleGroups.find(({ name }) => name === roleGroupFilter);

    if (matchingRoleGroup) {
        return roleGroupFilter;
    }

    return 'all';
};

internals.GroupDetailsHeaderControlsForm = function GroupDetailsHeaderControlsForm(props) {

    const {
        availableRoleGroups,
        roleGroupFilter,
        setRoleGroupFilter
    } = props;
    const roleOptions = internals.buildRoleOptions(availableRoleGroups);

    if (!roleOptions.length) {
        return null;
    }

    const formConfig = {
        formId: 'group-detail-role-filter-form',
        defaults: {
            roleGroupFilter: internals.resolveRoleGroupFilterValue({
                availableRoleGroups,
                roleGroupFilter
            })
        },
        fields: [{
            type: 'group',
            fields: [{
                name: 'roleGroupFilter',
                type: 'SelectField',
                label: 'Role',
                options: roleOptions,
                onChange: (value) => {

                    setRoleGroupFilter(value?.filter || value);
                },
                textFieldProps: {
                    variant: 'filled',
                    fieldStyles: internals.filterFieldStyles,
                    InputLabelProps: {
                        shrink: true
                    },
                    InputProps: {
                        disableUnderline: true
                    },
                    inputProps: {
                        'aria-label': 'Role'
                    }
                }
            }]
        }],
        onSubmit: internals.noop
    };
    const { Form } = useForm(formConfig);

    return <internals.RoleGroupFilterWrapper>
        {Form}
    </internals.RoleGroupFilterWrapper>;
};

internals.GroupDetailsHeaderControlsForm.propTypes = {
    availableRoleGroups: T.array,
    roleGroupFilter: T.string,
    setRoleGroupFilter: T.func.isRequired
};

// eslint-disable-next-line
internals.groupListSortCompare = (badges) => (peer1, peer2) => {

    const hasBadge = (type) => {

        return badges && badges.find(({ name }) => name === type);
    };

    // push users with badges to the front of the list
    if (hasBadge(peer1.type) !== hasBadge(peer2.type)) {
        return hasBadge(peer1.type) ? -1 : 1;
    }

    // sort alphabetically
    return (`${peer1.firstName.toLowerCase()} ${peer1.lastName.toLowerCase()}` < `${peer2.firstName.toLowerCase()} ${peer2.lastName.toLowerCase()}`) ? -1 : 1;
};

internals.isSelectable = (user) => {

    if (!user.isMe && user.peerStatus === null) {
        return true;
    }

    return false;
};

internals.styles = {
    joinButton: {
        height: 36,
        width: 'auto',
        margin: 0
    },
    joinFab: {
        height: '40px',
        width: '40px',
        minWidth: '40px',
        margin: 0,
        borderRadius: '50%'
    },
    announcementFab: (isAnnouncement) => ({
        backgroundColor: isAnnouncement ? 'rgb(255, 64, 129)' : '#1D0050',
        color: isAnnouncement ? '#000000' : '#ffffff'
    }),
    shrinkHeader: {
        maxHeight: 0
    },
    buttonLabel: {
        fontSize: '12px'
    },
    button: {
        width: 'calc(100% - 64px)',
        display: 'flex',
        lineHeight: '34px'
    }
};

internals.Badge = styled(MuiBadge)`
    margin: 0 9px;
    .MuiBadge-badge {
        border: 1px solid rgba(255, 255, 255, 0.5);
    }
`;

internals.InlineTextField = function InlineTextField(props) {

    return <TextField
        {...props}

        InputProps={{
            classes: {
                root: Classes.muiInputRoot
            }
        }}
    />;
};

internals.RoleGroupFilterWrapper = styled.div`
    width: 100%;
    box-sizing: border-box;
    padding: 20px 20px 0;
    background: white;
    display: flex;
    justify-content: center;

    form {
        max-width: 560px;
        align-items: center;
        gap: 0;
    }

    form > div:first-child {
        width: 100%;
        display: flex !important;
        flex-flow: row nowrap !important;
        align-items: center;
        justify-content: center;
        gap: 12px !important;
    }

    form > div:first-child > * {
        flex: 0 0 150px;
        max-width: 150px;
    }

    @media screen and (max-width: 430px) {
        form > div:first-child {
            gap: 8px !important;
        }
    }
`;

internals.filterFieldStyles = css`
    &.MuiFormControl-root {
        width: 150px;
        min-width: 150px;
        padding: 0 !important;
    }

    .MuiFilledInput-root {
        height: 48px;
        border-radius: 10px;
        background: rgba(18, 39, 79, 0.06);
        overflow: hidden;

        &:before,
        &:after,
        &:hover:not(.Mui-disabled):before {
            display: none;
        }

        &:hover,
        &.Mui-focused {
            background: rgba(18, 39, 79, 0.08);
        }
    }

    label.MuiInputLabel-root {
        max-width: calc(100% - 24px);
        margin-top: 0 !important;
        height: 16px !important;
        line-height: 16px !important;
        color: rgba(18, 39, 79, 0.56);
        font-size: 12px;
        transform: translate(12px, 5px) scale(1) !important;
        pointer-events: none;
    }

    .MuiInputLabel-shrink {
        transform: translate(12px, 5px) scale(1) !important;
    }

    .MuiSelect-select.MuiFilledInput-input {
        min-height: 20px;
        padding: 22px 32px 6px 12px !important;
        background: transparent;
        color: ${({ theme }) => theme.palette.text.primary};
        font-size: 16px;
        line-height: 20px;
    }

    .MuiSvgIcon-root {
        right: 8px;
        color: rgba(18, 39, 79, 0.56);
    }
`;

internals.DetailsStateWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 36px 20px 18px;
    box-sizing: border-box;
`;

internals.DetailsStatePill = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 28px;
    width: fit-content;
    max-width: min(520px, calc(100vw - 48px));
    margin: 0 auto;
    padding: 0 12px;
    border-radius: 999px;
    background: rgba(18, 39, 79, 0.06);
    color: ${({ theme }) => theme.palette.text.primary};
    text-align: center;
    font-size: 18px;
    line-height: 22px;
    font-weight: 800;
    letter-spacing: -0.015em;
    white-space: pre-line;

    @media screen and (max-width: 600px) {
        max-width: calc(100vw - 32px);
        font-size: 16px;
        line-height: 20px;
    }
`;

internals.DetailsLoadingPill = styled(internals.DetailsStatePill)`
    margin-bottom: 8px;
`;

internals.DetailsLoaderWrapper = styled.div`
    min-height: 180px;
    padding: 32px 20px 18px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;

    .nearpeer-loading-screen--content {
        height: auto !important;
        min-height: 96px !important;
    }
`;
